import { Check, Undo2, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { fmtDateTime } from '../../../shared/format'
import { useCancelAssignment, useDecideAssignment, type AssignmentRequest } from '../../sprintApi'
import { toastError, useStaffLookup } from '../../ui'

const STATUS_TONE = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'neutral' } as const
const DECISION_TONE = { '': 'neutral', approved: 'success', rejected: 'danger' } as const

/** Atama talepleri: rol bazında onay durumu, gerekçe; onaycı için onayla/reddet, talep eden için geri çek. */
export function AssignmentRequestList({ items, showTask }: { items: AssignmentRequest[]; showTask?: boolean }) {
  return (
    <ul className="space-y-3">
      {items.map((r) => (
        <RequestCard key={r.id} r={r} showTask={showTask} />
      ))}
    </ul>
  )
}

function RequestCard({ r, showTask }: { r: AssignmentRequest; showTask?: boolean }) {
  const { t } = useTranslation()
  const { nameOf } = useStaffLookup()
  const decide = useDecideAssignment()
  const cancel = useCancelAssignment()
  const [note, setNote] = useState('')
  const run = (decision: 'approved' | 'rejected') =>
    decide.mutate(
      { id: r.id, decision, note },
      {
        onSuccess: () => {
          toast.success(t(`projects.assignments.done.${decision}`))
          setNote('')
        },
        onError: toastError,
      },
    )
  return (
    <li className="space-y-3 rounded-xl border bg-card p-4 shadow-card">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={STATUS_TONE[r.status]}>{t(`projects.assignments.status.${r.status}`)}</Badge>
        {showTask ? (
          <Link to={`/projects/${r.projectId}/tasks/${r.taskId}`} className="font-medium text-primary hover:underline">
            {r.taskKey ? `${r.taskKey} · ` : ''}
            {r.taskTitle}
          </Link>
        ) : null}
        <span className="text-sm">{t('projects.assignments.summary', { requester: nameOf(r.requestedBy), assignee: nameOf(r.assigneeId) })}</span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">{fmtDateTime(r.createdAt)}</span>
      </div>
      {r.reason ? <p className="whitespace-pre-wrap rounded-lg bg-muted/40 p-2 text-sm">{r.reason}</p> : null}
      <div className="flex flex-wrap gap-2">
        {r.approvals.map((a) => (
          <div key={a.role} className="rounded-lg border px-3 py-1.5 text-xs">
            <span className="font-medium">{t(`projects.assignments.roles.${a.role}`)}</span>{' '}
            <Badge tone={DECISION_TONE[a.decision]} className="ml-1">
              {t(`projects.assignments.decision.${a.decision || 'pending'}`)}
            </Badge>
            <span className="ml-1 text-muted-foreground">{a.decidedBy ? nameOf(a.decidedBy) : a.approvers.map(nameOf).join(', ')}</span>
            {a.note ? <span className="block text-muted-foreground">“{a.note}”</span> : null}
          </div>
        ))}
      </div>
      {r.myRoles.length ? (
        <div className="space-y-2 border-t pt-3">
          <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('projects.assignments.notePlaceholder')} />
          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" className="text-destructive" disabled={!note.trim() || decide.isPending} onClick={() => run('rejected')}>
              <X />
              {t('projects.assignments.reject')}
            </Button>
            <Button disabled={decide.isPending} onClick={() => run('approved')}>
              <Check />
              {t('projects.assignments.approve', { roles: r.myRoles.map((x) => t(`projects.assignments.roles.${x}`)).join(', ') })}
            </Button>
          </div>
        </div>
      ) : null}
      {r.canCancel ? (
        <div className="flex justify-end">
          <Button size="sm" variant="ghost" disabled={cancel.isPending} onClick={() => cancel.mutate(r.id, { onSuccess: () => toast.success(t('projects.assignments.cancelled')), onError: toastError })}>
            <Undo2 />
            {t('projects.assignments.cancel')}
          </Button>
        </div>
      ) : null}
    </li>
  )
}
